import type { FC } from 'react';
import React, { useContext, useCallback, useEffect, useState, Fragment, useMemo } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import type { ApolloError } from 'apollo-client';
import { useQuery } from '@apollo/react-hooks';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import { N200 } from '@atlaskit/theme/colors';
import Button from '@atlaskit/button/custom-theme-button';

import { InfiniteScroll } from '@confluence/infinite-scroll';
import {
	withTransparentErrorBoundary,
	ErrorDisplay,
	Attribution,
} from '@confluence/error-boundary';

import { FeedCardContext } from '../FeedCard/FeedCardContext';

import { ActivityLineUpdateDetail } from './ActivityLineUpdateDetail';
import { FeedCardAllActivityQuery } from './FeedCardAllActivityQuery.graphql';
import { AllActivityError } from './AllActivityError';
import { ActivityLineUpdateDetailSkeleton } from './ActivityLineUpdateDetailSkeleton';
import { getSupportedUpdateType, getAvatarUrl, getUpdateContentUrl } from './activityLineHelpers';
import type {
	FeedCardAllActivityQuery as FeedCardAllActivityQueryType,
	FeedCardAllActivityQueryVariables,
} from './__types__/FeedCardAllActivityQuery';
import { useChoreographerAssets } from '@confluence/choreographer-services';

const i18n = defineMessages({
	otherActions: {
		id: 'feed.card.otherActions',
		defaultMessage: '{num} recent {num, plural, one {action} other {actions} }',
		description: 'Text on the action summary text for the other actions button',
	},
});

// Inline dialog component wraps this with 16px 24px padding by default,
// so we need to compensate for that with a negative margin
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionDialog = styled.div({
	width: '316px',
	maxHeight: '235px',
	overflowY: 'auto',
	overflowX: 'hidden',
	paddingLeft: token('space.200', '16px'),
	paddingRight: token('space.200', '16px'),
	paddingTop: token('space.200', '16px'),
	paddingBottom: token('space.025', '2px'),
});

const buttonTheme = (currentTheme, themeProps) => {
	const { buttonStyles, spinnerStyles } = currentTheme(themeProps);
	return {
		buttonStyles: {
			...buttonStyles,
			marginRight: token('space.050', '4px'),
			fontWeight: token('font.weight.regular'),
			color: token('color.text.subtle', N200),
		},
		spinnerStyles,
	};
};

type Props = {
	contentUrl: string;
	contentId: string;
	recentActionsCount: number;
};

const AllActivityDropdownComponent: FC<Props> = ({ contentUrl, contentId, recentActionsCount }) => {
	const { fireAnalyticsEvent } = useContext(FeedCardContext);

	const [hovered, setHovered] = useState(false);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [nextPageError, setNextPageError] = useState<ApolloError | null>();
	const thirtyDaysAgo = useMemo(() => {
		const currentDate = new Date();
		const date = new Date(currentDate.setDate(currentDate.getDate() - 30));
		return date.toISOString();
	}, []);

	const {
		data,
		error: firstPageError,
		loading,
		fetchMore,
	} = useQuery<FeedCardAllActivityQueryType, FeedCardAllActivityQueryVariables>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		FeedCardAllActivityQuery,
		{
			variables: { contentId, fromDate: thirtyDaysAgo },
			skip: !(dialogOpen || hovered),
		},
	);

	const error = firstPageError || nextPageError;
	const hasNextPage = data?.pageActivity?.pageInfo?.hasNextPage;
	const endCursor = data?.pageActivity?.pageInfo?.endCursor;

	const onHover = useCallback(() => {
		if (!hovered) {
			setHovered(true);
		}
	}, [setHovered, hovered]);

	const toggleDialog = useCallback(() => {
		setDialogOpen((open) => !open);
	}, [setDialogOpen]);

	const closeDialog = useCallback(() => {
		setDialogOpen(false);
	}, [setDialogOpen]);

	const updates = data?.pageActivity.nodes || [];

	useEffect(() => {
		if (dialogOpen) {
			fireAnalyticsEvent({
				type: 'sendTrackEvent',
				action: 'more',
				actionSubject: 'recentActions',
				actionSubjectId: 'feedCardRecentActionsPeak',
			});
		}
	}, [dialogOpen, fireAnalyticsEvent]);

	const handlePagination = useCallback(async () => {
		try {
			await fetchMore({
				variables: { contentId, after: endCursor, fromDate: thirtyDaysAgo },
				updateQuery: (prevResults, { fetchMoreResult }) => {
					if (!fetchMoreResult) {
						return prevResults;
					}
					fetchMoreResult.pageActivity.nodes = prevResults.pageActivity.nodes.concat(
						fetchMoreResult.pageActivity.nodes,
					);
					return fetchMoreResult;
				},
			});
		} catch (e) {
			setNextPageError(e);
		}
	}, [fetchMore, endCursor, contentId, thirtyDaysAgo]);

	const renderEndOfActivityList = () => {
		if (error) {
			return (
				<ErrorDisplay error={error}>
					<AllActivityError />
				</ErrorDisplay>
			);
		}

		if (!loading && hasNextPage && endCursor) {
			return (
				<InfiniteScroll
					onScrollTo={handlePagination}
					cursor={endCursor}
					observerOptions={{ root: null, threshold: 0.05 }}
				>
					<ActivityLineUpdateDetailSkeleton />
				</InfiniteScroll>
			);
		}

		return null;
	};

	const renderDialogBody = () => {
		if (loading) {
			return <ActivityLineUpdateDetailSkeleton />;
		}

		return (
			<Fragment>
				{updates.map((update) => {
					if (update) {
						const type = getSupportedUpdateType(update.actionSubject, update.action);
						if (type) {
							const userId = update.user?.id || null;
							const avatarUrl = getAvatarUrl(update.user?.photos || null);
							const name = update.user?.displayName || null;

							const actionTime = update.timestamp;
							const link = getUpdateContentUrl(update, contentUrl, contentId);

							return (
								<ActivityLineUpdateDetail
									key={`${type}-${userId}-${actionTime}`}
									type={type}
									actionTime={actionTime}
									userId={userId}
									avatarUrl={avatarUrl}
									userDisplayName={name}
									link={link}
									groupSize={update.groupSize}
								/>
							);
						}
					}

					return null;
				})}

				{renderEndOfActivityList()}
			</Fragment>
		);
	};

	const { Popup } = useChoreographerAssets();

	return (
		<Popup
			isOpen={dialogOpen}
			content={() => <ActionDialog>{renderDialogBody()}</ActionDialog>}
			onClose={closeDialog}
			placement="bottom-end"
			trigger={(triggerProps) => (
				<Button
					onClick={toggleDialog}
					onMouseEnter={onHover}
					theme={buttonTheme}
					appearance="subtle-link"
					spacing="none"
					{...triggerProps}
				>
					<FormattedMessage {...i18n.otherActions} values={{ num: recentActionsCount }} />
				</Button>
			)}
		/>
	);
};

export const AllActivityDropdown = withTransparentErrorBoundary({
	attribution: Attribution.SMARTS,
})(AllActivityDropdownComponent);
